import { ref, computed, onUnmounted, type Ref, type ComputedRef } from 'vue';
import { debounce } from 'lodash';
import { useDeletedDutyStore } from '@/rennovation/planning-view/store';
import { useUserStore } from '@/rennovation/planning-view/store';
import type { DeletedDuty } from '@/rennovation/planning-view/types/api/deleted-duty-response';

interface UseDeletedDutiesReturn {
	searchText: Ref<string>;
	filteredDuties: ComputedRef<DeletedDuty[]>;
	handleInfiniteScroll: (event: Event) => Promise<void>;
	initialize: () => Promise<void>;
	handleSearchInput: (val: string) => void;
	store: ReturnType<typeof useDeletedDutyStore>;
}

export function useDeletedDuties(): UseDeletedDutiesReturn {
	const store = useDeletedDutyStore();
	const userStore = useUserStore();

	const DEBOUNCE_TIME = 300;

	const searchText = ref<string>('');
	const debouncedSearch = ref<string>('');

	const updateSearch = debounce((val: string): void => {
		debouncedSearch.value = val;
	}, DEBOUNCE_TIME);

	const handleSearchInput = (val: string): void => {
		updateSearch(val);
	};

	const filteredDuties = computed(() => {
		const q = debouncedSearch.value.trim().toLowerCase();
		const duties: DeletedDuty[] = store.duties;

		if (!q) return duties;
		return duties.filter((d) => d.name.toLowerCase().includes(q));
	});

	const handleInfiniteScroll = async (event: Event): Promise<void> => {
		const el = event.target as HTMLElement;
		const thresholdToLoadNextChunk = 50;

		const isBottom = el.scrollHeight - el.clientHeight - el.scrollTop <= thresholdToLoadNextChunk;

		if (isBottom && !store.loading && !store.allLoaded) {
			await store.loadChunk();
		}
	};

	const initialize = async (): Promise<void> => {
		const tasks: Promise<void>[] = [];

		if (Object.keys(userStore.usersById).length === 0) {
			tasks.push(userStore.fetchUsers());
		}

		store.reset();
		tasks.push(store.loadChunk());

		await Promise.all(tasks);
	};

	onUnmounted(() => {
		updateSearch.cancel();
		store.reset();
	});

	return {
		searchText,
		filteredDuties,
		handleInfiniteScroll,
		initialize,
		handleSearchInput,
		store,
	};
}
